import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Trophy, Copy, Check } from "lucide-react";

interface AddEliteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface CreatedUser {
  email: string;
  temp_password: string;
  trial_ends: string;
}

export default function AddEliteUserDialog({ open, onOpenChange, onSuccess }: AddEliteUserDialogProps) {
  const { session } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdUser, setCreatedUser] = useState<CreatedUser | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    if (!email || !session) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-create-user", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { email, password: password || undefined },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setCreatedUser({
        email: data.email,
        temp_password: data.temp_password,
        trial_ends: data.trial_ends,
      });
      toast.success("Elite user created successfully!");
      onSuccess();
    } catch (e: any) {
      toast.error(e.message || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!createdUser) return;
    navigator.clipboard.writeText(
      `Email: ${createdUser.email}\nPassword: ${createdUser.temp_password}`
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setEmail("");
    setPassword("");
    setCreatedUser(null);
    setCopied(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            Add Elite User
          </DialogTitle>
          <DialogDescription>
            Create a new user with Elite-tier subscription (30-day free trial).
          </DialogDescription>
        </DialogHeader>

        {!createdUser ? (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password (optional)</Label>
                <Input
                  id="password"
                  type="text"
                  placeholder="Auto-generated if blank"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to auto-generate a temporary password.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleCreate} disabled={!email || loading} className="gap-2">
                {loading ? "Creating..." : "Create Elite User"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="space-y-4 py-4">
            <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
              <p className="text-sm text-muted-foreground">Credentials:</p>
              <p className="text-sm font-medium text-foreground">Email: {createdUser.email}</p>
              <p className="text-sm font-medium text-foreground">Password: {createdUser.temp_password}</p>
              <p className="text-sm text-muted-foreground">
                Trial ends: {new Date(createdUser.trial_ends).toLocaleDateString()}
              </p>
            </div>
            <Button variant="outline" onClick={handleCopy} className="w-full gap-2">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied!" : "Copy Credentials"}
            </Button>
            <DialogFooter>
              <Button onClick={handleClose} className="w-full">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
